import React, { useState, useEffect } from "react";
import {
  Modal,
  Box,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormLabel,
  Chip,
  IconButton,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Checkbox,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import axios from "axios";
import API_ENDPOINTS from "config/api";
import { useMaterialUIController } from "context";

function RegistrarCliente({ open, onClose, onSuccess }) {
  const [controller] = useMaterialUIController();
  const { sidenavColor } = controller;

  // Estados para datos del formulario
  const [formData, setFormData] = useState({
    empresa_id: "",
    tipo_cliente: "persona",
    tipo_documento: "CI",
    rif: "",
    nombre: "",
    email: "",
    telefono: "",
    direccion: "",
    pais: "",
    ciudad: "",
    codigo_postal: "",
    tipo: "minorista",
    moneda: "USD",
    lista_precios: "",
    descuento_defecto: 0,
    condicion_pago: "contado",
    limite_credito: 0,
    vendedor_id: "",
    nombre_fiscal: "",
    direccion_fiscal: "",
    identificacion_fiscal: "",
    email_facturacion: "",
    notas_internas: "",
    estatus: "activo",
  });

  const [etiquetas, setEtiquetas] = useState([]);
  const [nuevaEtiqueta, setNuevaEtiqueta] = useState("");
  const [contactos, setContactos] = useState([
    { nombre: "", cargo: "", email: "", telefono: "", es_principal: false },
  ]);

  // Estados para datos de selects
  const [empresas, setEmpresas] = useState([]);
  const [vendedores, setVendedores] = useState([]);
  const [loading, setLoading] = useState(false);

  // Cargar empresas y vendedores al montar
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [empresasRes, vendedoresRes] = await Promise.all([
          axios.get(API_ENDPOINTS.EMPRESAS),
          axios.get(API_ENDPOINTS.USUARIOS),
        ]);
        setEmpresas(empresasRes.data);
        setVendedores(vendedoresRes.data.filter((u) => u.rol === "vendedor"));
      } catch (error) {
        console.error("Error cargando datos:", error);
      }
    };
    if (open) {
      fetchData();
    }
  }, [open]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleAgregarEtiqueta = () => {
    if (nuevaEtiqueta.trim() && !etiquetas.includes(nuevaEtiqueta.trim())) {
      setEtiquetas([...etiquetas, nuevaEtiqueta.trim()]);
      setNuevaEtiqueta("");
    }
  };

  const handleEliminarEtiqueta = (etiqueta) => {
    setEtiquetas(etiquetas.filter((e) => e !== etiqueta));
  };

  const handleAgregarContacto = () => {
    setContactos([
      ...contactos,
      { nombre: "", cargo: "", email: "", telefono: "", es_principal: false },
    ]);
  };

  const handleEliminarContacto = (index) => {
    setContactos(contactos.filter((_, i) => i !== index));
  };

  const handleContactoChange = (index, field, value) => {
    const nuevosContactos = [...contactos];
    if (field === "es_principal" && value) {
      // Solo un contacto puede ser principal
      nuevosContactos.forEach((c, i) => {
        c.es_principal = i === index;
      });
    } else {
      nuevosContactos[index][field] = value;
    }
    setContactos(nuevosContactos);
  };

  const handleSubmit = async (e, crearOtro = false) => {
    e.preventDefault();
    setLoading(true);

    try {
      const dataToSend = {
        ...formData,
        etiquetas,
        contactos: contactos.filter((c) => c.nombre.trim() !== ""),
      };

      const response = await axios.post(API_ENDPOINTS.CLIENTES, dataToSend);

      alert("Cliente registrado exitosamente");

      if (crearOtro) {
        // Limpiar formulario pero mantener empresa_id
        const empresaId = formData.empresa_id;
        resetForm();
        setFormData((prev) => ({ ...prev, empresa_id: empresaId }));
      } else {
        resetForm();
        onClose();
      }

      if (onSuccess) onSuccess(response.data.cliente);
    } catch (error) {
      console.error("Error al registrar cliente:", error);
      if (error.response?.data?.duplicate) {
        alert("Ya existe un cliente con este documento en la empresa seleccionada");
      } else if (error.response?.data?.errors) {
        const errores = Object.values(error.response.data.errors).flat().join("\n");
        alert(`Errores de validación:\n${errores}`);
      } else {
        alert("Error al registrar cliente");
      }
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      empresa_id: "",
      tipo_cliente: "persona",
      tipo_documento: "CI",
      rif: "",
      nombre: "",
      email: "",
      telefono: "",
      direccion: "",
      pais: "",
      ciudad: "",
      codigo_postal: "",
      tipo: "minorista",
      moneda: "USD",
      lista_precios: "",
      descuento_defecto: 0,
      condicion_pago: "contado",
      limite_credito: 0,
      vendedor_id: "",
      nombre_fiscal: "",
      direccion_fiscal: "",
      identificacion_fiscal: "",
      email_facturacion: "",
      notas_internas: "",
      estatus: "activo",
    });
    setEtiquetas([]);
    setContactos([{ nombre: "", cargo: "", email: "", telefono: "", es_principal: false }]);
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  return (
    <Modal open={open} onClose={handleClose}>
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: "90%",
          maxWidth: "900px",
          maxHeight: "90vh",
          overflow: "auto",
          bgcolor: "background.paper",
          borderRadius: "8px",
          boxShadow: 24,
          p: 0,
        }}
      >
        <form onSubmit={handleSubmit}>
          {/* Header */}
          <MDBox
            bgColor={sidenavColor}
            borderRadius="8px 8px 0 0"
            p={3}
            display="flex"
            justifyContent="center"
          >
            <MDTypography variant="h5" color="white" fontWeight="medium">
              Registrar Nuevo Cliente
            </MDTypography>
          </MDBox>

          <MDBox p={3}>
            {/* Datos Básicos */}
            <Accordion defaultExpanded>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="h6">Datos Básicos *</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <MDBox display="flex" flexDirection="column" gap={2}>
                  <FormControl fullWidth required>
                    <InputLabel>Empresa Asociada</InputLabel>
                    <Select
                      name="empresa_id"
                      value={formData.empresa_id}
                      onChange={handleChange}
                      label="Empresa Asociada"
                    >
                      {empresas.map((emp) => (
                        <MenuItem key={emp.id} value={emp.id}>
                          {emp.nombre}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  <FormControl component="fieldset">
                    <FormLabel>Tipo de Cliente</FormLabel>
                    <RadioGroup
                      row
                      name="tipo_cliente"
                      value={formData.tipo_cliente}
                      onChange={handleChange}
                    >
                      <FormControlLabel value="persona" control={<Radio />} label="Persona" />
                      <FormControlLabel value="empresa" control={<Radio />} label="Empresa" />
                    </RadioGroup>
                  </FormControl>

                  <TextField
                    fullWidth
                    required
                    label="Nombre o Razón Social"
                    name="nombre"
                    value={formData.nombre}
                    onChange={handleChange}
                  />

                  <MDBox display="flex" gap={2}>
                    <FormControl sx={{ minWidth: 150 }} required>
                      <InputLabel>Tipo Documento</InputLabel>
                      <Select
                        name="tipo_documento"
                        value={formData.tipo_documento}
                        onChange={handleChange}
                        label="Tipo Documento"
                      >
                        <MenuItem value="CI">CI</MenuItem>
                        <MenuItem value="RIF">RIF</MenuItem>
                        <MenuItem value="Pasaporte">Pasaporte</MenuItem>
                        <MenuItem value="DNI">DNI</MenuItem>
                        <MenuItem value="NIF">NIF</MenuItem>
                        <MenuItem value="VAT">VAT</MenuItem>
                        <MenuItem value="RUC">RUC</MenuItem>
                      </Select>
                    </FormControl>
                    <TextField
                      fullWidth
                      required
                      label="Número de Documento"
                      name="rif"
                      value={formData.rif}
                      onChange={handleChange}
                    />
                  </MDBox>

                  <MDBox display="flex" gap={2}>
                    <TextField
                      fullWidth
                      label="Email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                    />
                    <TextField
                      fullWidth
                      label="Teléfono"
                      name="telefono"
                      value={formData.telefono}
                      onChange={handleChange}
                    />
                  </MDBox>

                  <FormControl fullWidth>
                    <InputLabel>Estado</InputLabel>
                    <Select
                      name="estatus"
                      value={formData.estatus}
                      onChange={handleChange}
                      label="Estado"
                    >
                      <MenuItem value="activo">Activo</MenuItem>
                      <MenuItem value="inactivo">Inactivo</MenuItem>
                    </Select>
                  </FormControl>
                </MDBox>
              </AccordionDetails>
            </Accordion>

            {/* Dirección */}
            <Accordion>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="h6">Dirección</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <MDBox display="flex" flexDirection="column" gap={2}>
                  <TextField
                    fullWidth
                    label="Dirección"
                    name="direccion"
                    multiline
                    rows={2}
                    value={formData.direccion}
                    onChange={handleChange}
                  />
                  <MDBox display="flex" gap={2}>
                    <TextField
                      fullWidth
                      label="País"
                      name="pais"
                      value={formData.pais}
                      onChange={handleChange}
                    />
                    <TextField
                      fullWidth
                      label="Ciudad/Provincia"
                      name="ciudad"
                      value={formData.ciudad}
                      onChange={handleChange}
                    />
                  </MDBox>
                  <TextField
                    fullWidth
                    label="Código Postal"
                    name="codigo_postal"
                    value={formData.codigo_postal}
                    onChange={handleChange}
                  />
                </MDBox>
              </AccordionDetails>
            </Accordion>

            {/* Datos Comerciales */}
            <Accordion>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="h6">Datos Comerciales</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <MDBox display="flex" flexDirection="column" gap={2}>
                  <MDBox display="flex" gap={2}>
                    <FormControl fullWidth>
                      <InputLabel>Moneda</InputLabel>
                      <Select
                        name="moneda"
                        value={formData.moneda}
                        onChange={handleChange}
                        label="Moneda"
                      >
                        <MenuItem value="USD">USD - Dólar</MenuItem>
                        <MenuItem value="VES">VES - Bolívar</MenuItem>
                        <MenuItem value="EUR">EUR - Euro</MenuItem>
                      </Select>
                    </FormControl>
                    <FormControl fullWidth>
                      <InputLabel>Tipo de Cliente</InputLabel>
                      <Select
                        name="tipo"
                        value={formData.tipo}
                        onChange={handleChange}
                        label="Tipo de Cliente"
                      >
                        <MenuItem value="minorista">Minorista</MenuItem>
                        <MenuItem value="mayorista">Mayorista</MenuItem>
                        <MenuItem value="corporativo">Corporativo</MenuItem>
                      </Select>
                    </FormControl>
                  </MDBox>

                  <MDBox display="flex" gap={2}>
                    <TextField
                      fullWidth
                      label="Lista de Precios/Tarifa"
                      name="lista_precios"
                      value={formData.lista_precios}
                      onChange={handleChange}
                    />
                    <TextField
                      fullWidth
                      label="Descuento por Defecto (%)"
                      name="descuento_defecto"
                      type="number"
                      inputProps={{ min: 0, max: 100, step: 0.01 }}
                      value={formData.descuento_defecto}
                      onChange={handleChange}
                    />
                  </MDBox>

                  <MDBox display="flex" gap={2}>
                    <FormControl fullWidth>
                      <InputLabel>Condición de Pago</InputLabel>
                      <Select
                        name="condicion_pago"
                        value={formData.condicion_pago}
                        onChange={handleChange}
                        label="Condición de Pago"
                      >
                        <MenuItem value="contado">Contado</MenuItem>
                        <MenuItem value="15_dias">15 días</MenuItem>
                        <MenuItem value="30_dias">30 días</MenuItem>
                        <MenuItem value="60_dias">60 días</MenuItem>
                        <MenuItem value="90_dias">90 días</MenuItem>
                      </Select>
                    </FormControl>
                    <TextField
                      fullWidth
                      label="Límite de Crédito"
                      name="limite_credito"
                      type="number"
                      inputProps={{ min: 0, step: 0.01 }}
                      value={formData.limite_credito}
                      onChange={handleChange}
                    />
                  </MDBox>

                  <FormControl fullWidth>
                    <InputLabel>Vendedor Asignado</InputLabel>
                    <Select
                      name="vendedor_id"
                      value={formData.vendedor_id}
                      onChange={handleChange}
                      label="Vendedor Asignado"
                    >
                      <MenuItem value="">Sin asignar</MenuItem>
                      {vendedores.map((v) => (
                        <MenuItem key={v.id} value={v.id}>
                          {v.nombre}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </MDBox>
              </AccordionDetails>
            </Accordion>

            {/* Facturación */}
            <Accordion>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="h6">Facturación</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <MDBox display="flex" flexDirection="column" gap={2}>
                  <TextField
                    fullWidth
                    label="Nombre Fiscal"
                    name="nombre_fiscal"
                    value={formData.nombre_fiscal}
                    onChange={handleChange}
                  />
                  <TextField
                    fullWidth
                    label="Dirección Fiscal"
                    name="direccion_fiscal"
                    multiline
                    rows={2}
                    value={formData.direccion_fiscal}
                    onChange={handleChange}
                  />
                  <MDBox display="flex" gap={2}>
                    <TextField
                      fullWidth
                      label="Identificación Fiscal"
                      name="identificacion_fiscal"
                      value={formData.identificacion_fiscal}
                      onChange={handleChange}
                    />
                    <TextField
                      fullWidth
                      label="Email de Facturación"
                      name="email_facturacion"
                      type="email"
                      value={formData.email_facturacion}
                      onChange={handleChange}
                    />
                  </MDBox>
                </MDBox>
              </AccordionDetails>
            </Accordion>

            {/* Contactos */}
            <Accordion>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="h6">Contactos</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <MDBox display="flex" flexDirection="column" gap={2}>
                  {contactos.map((contacto, index) => (
                    <MDBox
                      key={index}
                      p={2}
                      border="1px solid #ddd"
                      borderRadius="8px"
                      position="relative"
                    >
                      {contactos.length > 1 && (
                        <IconButton
                          size="small"
                          onClick={() => handleEliminarContacto(index)}
                          sx={{ position: "absolute", top: 8, right: 8 }}
                        >
                          <DeleteIcon />
                        </IconButton>
                      )}
                      <MDBox display="flex" flexDirection="column" gap={2}>
                        <TextField
                          fullWidth
                          label="Nombre del Contacto"
                          value={contacto.nombre}
                          onChange={(e) => handleContactoChange(index, "nombre", e.target.value)}
                        />
                        <MDBox display="flex" gap={2}>
                          <TextField
                            fullWidth
                            label="Cargo"
                            value={contacto.cargo}
                            onChange={(e) => handleContactoChange(index, "cargo", e.target.value)}
                          />
                          <TextField
                            fullWidth
                            label="Email"
                            type="email"
                            value={contacto.email}
                            onChange={(e) => handleContactoChange(index, "email", e.target.value)}
                          />
                        </MDBox>
                        <MDBox display="flex" gap={2} alignItems="center">
                          <TextField
                            fullWidth
                            label="Teléfono"
                            value={contacto.telefono}
                            onChange={(e) =>
                              handleContactoChange(index, "telefono", e.target.value)
                            }
                          />
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={contacto.es_principal}
                                onChange={(e) =>
                                  handleContactoChange(index, "es_principal", e.target.checked)
                                }
                              />
                            }
                            label="Principal"
                          />
                        </MDBox>
                      </MDBox>
                    </MDBox>
                  ))}
                  <MDButton
                    variant="outlined"
                    color={sidenavColor}
                    startIcon={<AddIcon />}
                    onClick={handleAgregarContacto}
                  >
                    Agregar Contacto
                  </MDButton>
                </MDBox>
              </AccordionDetails>
            </Accordion>

            {/* Extras */}
            <Accordion>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="h6">Extras</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <MDBox display="flex" flexDirection="column" gap={2}>
                  <MDBox>
                    <Typography variant="body2" mb={1}>
                      Etiquetas (VIP, Mayorista, etc.)
                    </Typography>
                    <MDBox display="flex" gap={1} flexWrap="wrap" mb={1}>
                      {etiquetas.map((etiqueta, index) => (
                        <Chip
                          key={index}
                          label={etiqueta}
                          onDelete={() => handleEliminarEtiqueta(etiqueta)}
                          color="primary"
                        />
                      ))}
                    </MDBox>
                    <MDBox display="flex" gap={1}>
                      <TextField
                        fullWidth
                        size="small"
                        placeholder="Nueva etiqueta"
                        value={nuevaEtiqueta}
                        onChange={(e) => setNuevaEtiqueta(e.target.value)}
                        onKeyPress={(e) => {
                          if (e.key === "Enter") {
                            e.preventDefault();
                            handleAgregarEtiqueta();
                          }
                        }}
                      />
                      <MDButton
                        variant="outlined"
                        color={sidenavColor}
                        onClick={handleAgregarEtiqueta}
                      >
                        Agregar
                      </MDButton>
                    </MDBox>
                  </MDBox>

                  <TextField
                    fullWidth
                    label="Notas Internas"
                    name="notas_internas"
                    multiline
                    rows={3}
                    value={formData.notas_internas}
                    onChange={handleChange}
                  />
                </MDBox>
              </AccordionDetails>
            </Accordion>
          </MDBox>

          {/* Botones de acción */}
          <MDBox p={3} display="flex" justifyContent="flex-end" gap={2} borderTop="1px solid #ddd">
            <MDButton variant="outlined" color="secondary" onClick={handleClose}>
              Cancelar
            </MDButton>
            <MDButton
              variant="gradient"
              color={sidenavColor}
              onClick={(e) => handleSubmit(e, true)}
              disabled={loading}
            >
              Guardar y Crear Otro
            </MDButton>
            <MDButton type="submit" variant="gradient" color={sidenavColor} disabled={loading}>
              {loading ? "Guardando..." : "Guardar"}
            </MDButton>
          </MDBox>
        </form>
      </Box>
    </Modal>
  );
}

export default RegistrarCliente;
